// src/types/modulePermissions.ts
// Enhanced role system with module-based permissions

export type AccessLevel = 'none' | 'read' | 'write';

// Navigation modules that can have permissions
export type NavigationModule = 
  | 'dashboard'
  | 'staff_management' 
  | 'curriculum_areas'
  | 'class_allocation'
  | 'load_letter_system'
  | 'reports_analytics'
  | 'system_settings';

// Module permission configuration
export interface ModulePermission {
  module: NavigationModule;
  access: AccessLevel;
  description: string;
}

// Enhanced role definition with module permissions
export interface EnhancedRoleDefinition {
  id: string;
  name: string;
  description: string;
  level: number; // 1 (lowest) to 10 (highest)
  isCustom: boolean; // True for user-created roles
  isSystemAdmin: boolean; // True for full system admin access
  modulePermissions: ModulePermission[];
  // Legacy support
  canManageRoles?: string[]; // Which roles this role can manage
  createdAt?: Date;
  updatedAt?: Date;
}

// Module definitions with metadata
export interface ModuleDefinition {
  id: NavigationModule;
  name: string;
  description: string;
  icon: string; // Icon component name
  category: 'core' | 'academic' | 'administrative' | 'system';
  subModules?: string[]; // List of sub-pages/features
}

// Available modules in the system
export const MODULE_DEFINITIONS: ModuleDefinition[] = [
  {
    id: 'dashboard',
    name: 'Dashboard',
    description: 'Main dashboard with overview and quick access',
    icon: 'HomeIcon',
    category: 'core',
    subModules: ['Overview', 'Quick Actions', 'Notifications']
  },
  {
    id: 'staff_management',
    name: 'Staff Management',
    description: 'Manage staff directory, allowances, and teaching intentions',
    icon: 'UsersIcon',
    category: 'administrative',
    subModules: ['Staff Directory', 'Staff Allowances', 'Teaching Intentions', 'Load Summaries']
  },
  {
    id: 'curriculum_areas',
    name: 'Curriculum Areas',
    description: 'Manage curriculum preferences and subject area data',
    icon: 'BookOpenIcon',
    category: 'academic',
    subModules: ['Subject Area Preferences', 'Curriculum Grid View']
  },
  {
    id: 'class_allocation',
    name: 'Grid Management',
    description: 'Manage class scheduling, teacher assignments, and grid management',
    icon: 'GridIcon',
    category: 'academic',
    subModules: [
      'Grid Import', 'Grid Editor', 'Class Management', 'Staff Assignment',
      'Line Scheduling', 'Subject Configuration', 'AI Suggestions', 'Bulk Operations',
      'Conflict Resolution', 'Assignment History', 'Grid Export', 'Curriculum Publishing'
    ]
  },
  {
    id: 'load_letter_system',
    name: 'Load Letter System',
    description: 'Generate and manage teaching load letters',
    icon: 'FileTextIcon',
    category: 'administrative',
    subModules: ['Final Import', 'Load Calculation', 'Letter Generation', 'Staff Dashboard']
  },
  {
    id: 'reports_analytics',
    name: 'Reports & Analytics',
    description: 'Generate reports and analyze system data',
    icon: 'BarChartIcon',
    category: 'administrative',
    subModules: ['Preference Satisfaction', 'Workload Equity', 'Recruitment Gaps', 'Export Tools']
  },
  {
    id: 'system_settings',
    name: 'System Settings',
    description: 'Configure system-wide settings and manage users',
    icon: 'SettingsIcon',
    category: 'system',
    subModules: [
      'School Configuration', 'Departments', 'Allowance Templates', 'Teaching Load System',
      'User Management', 'Role Management', 'AI Configuration', 'Import/Export Mappings', 'System Backup'
    ]
  }
];

// Predefined system roles
export const SYSTEM_ROLES: EnhancedRoleDefinition[] = [
  {
    id: 'system_admin',
    name: 'System Administrator',
    description: 'Full system access with all permissions',
    level: 10,
    isCustom: false,
    isSystemAdmin: true,
    modulePermissions: MODULE_DEFINITIONS.map(module => ({
      module: module.id,
      access: 'write' as AccessLevel,
      description: `Full write access to ${module.name}`
    }))
  },
  {
    id: 'principal',
    name: 'Principal',
    description: 'School leadership with comprehensive access including system settings',
    level: 9,
    isCustom: false,
    isSystemAdmin: false,
    modulePermissions: [
      { module: 'dashboard', access: 'write', description: 'Full dashboard access' },
      { module: 'staff_management', access: 'write', description: 'Manage all staff' },
      { module: 'curriculum_areas', access: 'write', description: 'Manage all curriculum areas' },
      { module: 'class_allocation', access: 'write', description: 'Full grid management access' },
      { module: 'load_letter_system', access: 'write', description: 'Generate and manage load letters' },
      { module: 'reports_analytics', access: 'write', description: 'Generate all reports' },
      { module: 'system_settings', access: 'write', description: 'Full system configuration access' }
    ]
  },
  {
    id: 'assistant_principal',
    name: 'Assistant Principal',
    description: 'Curriculum oversight across all areas (read-only grids, no staff access)',
    level: 8,
    isCustom: false,
    isSystemAdmin: false,
    modulePermissions: [
      { module: 'dashboard', access: 'read', description: 'View dashboard and overview' },
      { module: 'staff_management', access: 'none', description: 'No access to staff directory' },
      { module: 'curriculum_areas', access: 'write', description: 'Manage curriculum preferences for ALL areas' },
      { module: 'class_allocation', access: 'none', description: 'No access to grid editing' },
      { module: 'load_letter_system', access: 'none', description: 'No access to load letters' },
      { module: 'reports_analytics', access: 'none', description: 'No access to reports' },
      { module: 'system_settings', access: 'none', description: 'No access to system settings' }
    ]
  },
  {
    id: 'head_of_department',
    name: 'Head of Department',
    description: 'Department oversight with curriculum management access across ALL curriculum areas',
    level: 6,
    isCustom: false,
    isSystemAdmin: false,
    modulePermissions: [
      { module: 'dashboard', access: 'read', description: 'View dashboard and overview' },
      { module: 'staff_management', access: 'none', description: 'No access to staff management' },
      { module: 'curriculum_areas', access: 'write', description: 'Manage curriculum preferences for ALL areas' },
      { module: 'class_allocation', access: 'none', description: 'No access to grid management' },
      { module: 'load_letter_system', access: 'none', description: 'No sidebar access to load letter system' },
      { module: 'reports_analytics', access: 'none', description: 'No access to reports' },
      { module: 'system_settings', access: 'none', description: 'No access to system settings' }
    ]
  },
  {
    id: 'curriculum_leader',
    name: 'Curriculum Leader',
    description: 'Subject area leadership with curriculum management access only',
    level: 5,
    isCustom: false,
    isSystemAdmin: false,
    modulePermissions: [
      { module: 'dashboard', access: 'read', description: 'View dashboard and overview' },
      { module: 'staff_management', access: 'none', description: 'No access to staff management' },
      { module: 'curriculum_areas', access: 'write', description: 'Manage curriculum preferences for their area' },
      { module: 'class_allocation', access: 'none', description: 'No access to grid management' },
      { module: 'load_letter_system', access: 'none', description: 'No sidebar access to load letter system' },
      { module: 'reports_analytics', access: 'none', description: 'No access to reports' },
      { module: 'system_settings', access: 'none', description: 'No access to system settings' }
    ]
  },
  {
    id: 'teacher',
    name: 'Teacher',
    description: 'Standard teaching staff can view their own load letter',
    level: 3,
    isCustom: false,
    isSystemAdmin: false,
    modulePermissions: [
      { module: 'dashboard', access: 'none', description: 'No dashboard access' },
      { module: 'staff_management', access: 'none', description: 'No staff management access' },
      { module: 'curriculum_areas', access: 'none', description: 'No curriculum access' },
      { module: 'class_allocation', access: 'none', description: 'No grid management access' },
      { module: 'load_letter_system', access: 'read', description: 'View own load letter only' },
      { module: 'reports_analytics', access: 'none', description: 'No access to reports' },
      { module: 'system_settings', access: 'none', description: 'No access to system settings' }
    ]
  }
];

// Helper functions
export const getModulePermission = (role: EnhancedRoleDefinition, module: NavigationModule): AccessLevel => {
  if (role.isSystemAdmin) return 'write';
  
  const permission = role.modulePermissions.find(p => p.module === module);
  return permission?.access || 'none';
};

export const hasModuleAccess = (role: EnhancedRoleDefinition, module: NavigationModule, requiredLevel: AccessLevel = 'read'): boolean => {
  const userLevel = getModulePermission(role, module);
  
  if (userLevel === 'none') return false;
  if (requiredLevel === 'read') return userLevel === 'read' || userLevel === 'write';
  if (requiredLevel === 'write') return userLevel === 'write';
  
  return false;
};

export const getModuleDefinition = (moduleId: NavigationModule): ModuleDefinition | undefined => {
  return MODULE_DEFINITIONS.find(module => module.id === moduleId);
};

export const getUserAccessibleModules = (role: EnhancedRoleDefinition): NavigationModule[] => {
  return role.modulePermissions
    .filter(permission => permission.access !== 'none')
    .map(permission => permission.module);
};

export const createCustomRole = (
  name: string, 
  description: string, 
  modulePermissions: ModulePermission[]
): EnhancedRoleDefinition => {
  return {
    id: `custom_${name.toLowerCase().replace(/\s+/g, '_')}_${Date.now()}`,
    name,
    description,
    level: 5, // Default level for custom roles
    isCustom: true,
    isSystemAdmin: false,
    modulePermissions,
    createdAt: new Date(),
    updatedAt: new Date()
  };
};